import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConnectStatus {
  account_id: string | null;
  onboarded: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
}

// ─── Hook: read org connect status from DB (no Stripe API call) ────────────────

export function useConnectStatus(orgId?: string | null) {
  const { activeOrgId } = useAuth();
  const id = orgId ?? activeOrgId;

  return useQuery({
    queryKey: ["connect_status", id],
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 min — don't hammer this
    queryFn: async (): Promise<ConnectStatus> => {
      const { data, error } = await supabase
        .from("organizations")
        .select(
          "stripe_connect_account_id, stripe_connect_onboarded, stripe_connect_charges_enabled, stripe_connect_payouts_enabled",
        )
        .eq("id", id!)
        .single();

      if (error) {
        // Migration may not have been applied yet — return safe default silently
        console.warn("[useConnectStatus] Stripe Connect columns not yet available:", error.message);
        return { account_id: null, onboarded: false, charges_enabled: false, payouts_enabled: false };
      }
      return {
        account_id: data.stripe_connect_account_id ?? null,
        onboarded: data.stripe_connect_onboarded ?? false,
        charges_enabled: data.stripe_connect_charges_enabled ?? false,
        payouts_enabled: data.stripe_connect_payouts_enabled ?? false,
      };
    },
  });
}

// ─── Helper: extract human-readable message from a Supabase FunctionsHttpError ─
// When an edge function returns a non-2xx response the Supabase client wraps it
// in a FunctionsHttpError whose `.context` property is the raw Response object.
// We read the JSON body to surface the `error` field we set in the edge function.
async function extractFnError(err: unknown, fallback: string): Promise<string> {
  try {
    const ctx = (err as any)?.context;
    if (ctx && typeof ctx.json === "function") {
      const body = await ctx.json();
      if (body?.error) return body.error as string;
    }
  } catch {
    // ignore — fall through to default message
  }
  return (err as any)?.message ?? fallback;
}

// ─── Hook: create / refresh the Stripe Connect account ───────────────────────

export function useCreateConnectAccount() {
  const queryClient = useQueryClient();
  const { activeOrgId, session } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (orgId?: string): Promise<ConnectStatus> => {
      const id = orgId ?? activeOrgId;
      if (!id) throw new Error("No active organization");

      const { data, error } = await supabase.functions.invoke("create-connect-account", {
        body: { org_id: id },
      });
      if (error) {
        const message = await extractFnError(error, "Failed to create Connect account — ensure the edge function is deployed and STRIPE_SECRET_KEY is set");
        throw new Error(message);
      }
      return data as ConnectStatus;
    },
    onSuccess: (data, orgId) => {
      const id = orgId ?? activeOrgId;
      queryClient.invalidateQueries({ queryKey: ["connect_status", id] });
      if (data.onboarded) {
        toast({ title: "Bank account connected!", description: "You can now receive payouts." });
      }
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ─── Hook: generate Stripe onboarding link ────────────────────────────────────

export function useConnectOnboardingLink() {
  const { activeOrgId, session } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (orgId?: string): Promise<{ url: string }> => {
      const id = orgId ?? activeOrgId;
      if (!id) throw new Error("No active organization");

      const { data, error } = await supabase.functions.invoke("create-connect-onboarding", {
        body: { org_id: id },
      });
      if (error) {
        const message = await extractFnError(error, "Failed to generate onboarding link — ensure the edge function is deployed");
        throw new Error(message);
      }
      return { url: (data as any).url };
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ─── Hook: create amenity payment (intent or checkout) ───────────────────────

export function useCreateAmenityPayment() {
  const { session } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      booking_id: string;
      mode?: "checkout" | "intent";
      success_url?: string;
      cancel_url?: string;
    }): Promise<{ url?: string; client_secret?: string }> => {
      const { data, error } = await supabase.functions.invoke("create-amenity-payment", {
        body: params,
      });
      if (error) {
        const message = await extractFnError(error, "Failed to initiate payment");
        throw new Error(message);
      }
      return data as { url?: string; client_secret?: string };
    },
    onError: (e: Error) =>
      toast({ title: "Payment error", description: e.message, variant: "destructive" }),
  });
}
